import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Play, Maximize2, X } from "lucide-react";
import { HERO_IMAGE, SANDRA_STAGE_IMAGE } from "../mock";
import { TrustpilotBadge } from "./TrustpilotBadge";
import { track } from "../lib/analytics";

const Hero = () => {
  const [lightbox, setLightbox] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goMatrix = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    track("take_matrix_click", { location: "hero" });
    if (location.pathname === "/matrix") {
      const el = document.getElementById("matrix");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/matrix");
    }
  };

  const goVideo = (e) => {
    e.preventDefault();
    track("watch_video_click", { location: "hero" });
    const el = document.getElementById("video");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden md:min-h-screen md:flex md:items-center">
      {/* DESKTOP — full-bleed gradient background; full figure pushed right */}
      <div className="hidden md:block absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Sandra, founder of Sassy Full Stack Fitness, age 56"
          className="absolute bottom-0 right-0 h-[78%] sm:h-[85%] lg:h-[96%] w-auto max-w-none object-contain object-bottom"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, #000 26%), linear-gradient(to bottom, transparent 0%, #000 14%)",
            WebkitMaskComposite: "source-in",
            maskImage:
              "linear-gradient(to right, transparent 0%, #000 26%), linear-gradient(to bottom, transparent 0%, #000 14%)",
            maskComposite: "intersect",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />

        {/* Photo caption — proof point */}
        <div className="absolute bottom-[12%] right-[3%] z-20 text-right pointer-events-none select-none" data-testid="hero-caption">
          <span className="block font-display font-black uppercase tracking-wide text-white text-xl lg:text-2xl leading-none drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
            Menopause<span className="text-gold-gradient">, Age 56</span>
          </span>
        </div>
      </div>

      {/* MOBILE — image blends into a full-bleed gradient above the headline */}
      <div className="md:hidden relative w-full h-[48vh] min-h-[320px]">
        <img
          src={HERO_IMAGE}
          alt="Sandra, founder of Sassy Full Stack Fitness"
          className="w-full h-full object-cover object-[50%_18%] brightness-110 contrast-[1.03]"
        />
        {/* blend into page background for a full-bleed gradient feel */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/10 to-background" />
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-background via-background/75 to-transparent" />
        {/* Primary CTA — centered gold pill */}
        <button
          onClick={goMatrix}
          className="absolute inset-x-0 bottom-[52px] z-30 mx-auto w-max rounded-full bg-primary text-primary-foreground font-bold text-sm tracking-wide px-7 py-3 shadow-[0_10px_32px_-6px_rgba(212,175,55,0.75)] active:scale-[0.97] transition"
          data-testid="hero-take-matrix-mobile"
        >
          Take the Matrix (Free)
        </button>
        {/* Tappable proof-point → opens full competition photo */}
        <button
          onClick={() => setLightbox(true)}
          className="absolute inset-x-0 bottom-1 z-20 mx-auto w-max flex items-center gap-2 rounded-full border border-primary/50 bg-black/45 backdrop-blur-sm px-4 py-2 text-primary text-[11px] font-bold uppercase tracking-[0.14em] active:scale-[0.97] transition"
          data-testid="hero-see-photo-mobile"
        >
          <Maximize2 size={13} />
          See Menopause at Age 56
        </button>
      </div>

      <div className="relative z-10 w-[90%] max-w-[1400px] mx-auto pt-2 pb-14 md:pt-28 md:pb-20">
        <div className="max-w-2xl fade-up">
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-white text-4xl sm:text-5xl md:text-6xl">
            Women over 40 who remember how they used to look and feel, and want both back.
          </h1>

          <p className="mt-5 font-display font-bold tracking-tight text-xl sm:text-2xl md:text-[1.75rem] leading-snug" style={{ color: "#D4AF35" }} data-testid="hero-tagline">
            Love who you see in the mirror again.
          </p>

          <p className="mt-5 text-lg md:text-xl text-white/75 leading-relaxed font-medium max-w-xl">
            You're not imagining it, and you're not doing it wrong. Your body changed the rules
            without telling you. The <strong className="font-bold italic text-white">Menopause&nbsp;Matrix<span className="not-italic font-semibold text-[0.68em]" style={{ verticalAlign: "0.62em", marginLeft: "0.1em" }}>™</span></strong> shows you exactly which systems moved
            specifically in you, in about 90 seconds.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <button
              onClick={goMatrix}
              className="inline-flex items-center rounded-full bg-primary text-primary-foreground font-bold text-base px-8 py-4 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)]"
            >
              Take the Matrix (Free)
            </button>
            <a
              href="#video"
              onClick={goVideo}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-semibold transition-colors focus-ring rounded-md"
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-full border border-white/25">
                <Play size={15} className="ml-0.5 fill-current" />
              </span>
              Watch the 4-min video
            </a>
          </div>

          {/* Fine print with inline Trustpilot footnote */}
          <p
            className="mt-4 text-sm text-white/55 font-medium leading-relaxed"
            data-testid="hero-trust-row"
          >
            No cost. No obligation. Your results are yours to keep.{" "}
            <span className="text-white/25" aria-hidden="true">·</span>{" "}
            <TrustpilotBadge className="align-middle" />
          </p>
        </div>
      </div>

      {/* Full competition photo lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setLightbox(false)}
          data-testid="hero-lightbox"
        >
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-5 right-5 text-white/80 hover:text-white transition-colors z-10"
            aria-label="Close"
            data-testid="hero-lightbox-close"
          >
            <X size={30} />
          </button>
          <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={SANDRA_STAGE_IMAGE}
              alt="Sandra competing on the NPC bikini stage at age 56"
              className="max-w-[92vw] max-h-[80vh] object-contain rounded-xl"
            />
            <p className="mt-5 text-center font-display font-black uppercase tracking-wide text-white text-lg md:text-xl">
              Menopause<span className="text-gold-gradient">, Age 56</span>
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
